/**
 * Turning report values into text a merchant reads.
 *
 * THE COUNTS ARRIVE AS STRINGS, AND THAT IS NOT AN ACCIDENT. ClickHouse encodes 64-bit
 * integers as JSON strings because a UInt64 does not survive a JavaScript number — past
 * 2^53 the value silently changes. apps/api keeps them as strings for exactly that
 * reason (API.md), so the LAST thing this layer may do is call `Number()` on one and
 * undo it. `formatCount` parses a whole-number string as a BigInt instead, which has no
 * ceiling, and only falls back to Number for values that are not integers.
 *
 * INDIAN GROUPING (en-IN), ported from `ecom_code`'s `formatIndianMetric` (owner request,
 * 2026-09-15): merchants here read 13,43,543, not 1,343,543.
 *
 * NO CURRENCY SYMBOL IS PRINTED. Neither the report rows nor `/auth/me` carry the
 * store's currency, so any symbol here would be a guess rendered as fact — and "₹1,200"
 * shown to a merchant billing in dollars is worse than an unlabelled number. Money is
 * formatted as a grouped number, and the screen says once, in words, that amounts are
 * in the store's own currency.
 */

/** What an empty cell looks like. One character, so a column of them reads as absent. */
const BLANK = "—";

const INTEGER = /^-?\d+$/;

const LOCALE = "en-IN";

/**
 * DECIMAL vs NON-DECIMAL, ported from `ecom_code`'s `formatIndianMetric` rule (owner
 * request, 2026-09-15):
 *
 *   - Rate-style money (CPS, CPA) keeps two decimals — these are per-unit costs, where
 *     2.24 and 2.25 are different decisions.
 *   - Fractional Purchases (`dda_orders`, `linear_orders`) keep two decimals — each is a
 *     SHARE of an order, not a whole one (see `COUNT_FORMAT`'s note, still true here).
 *   - Everything else money/count — Spend, Revenue, CPP, AOV, Orders, Sessions,
 *     Visitors, `triple_orders` (a WHOLE order per entity, never fractional) — is a
 *     Non-Decimal Integer Metric: rounded and grouped, no decimals.
 *
 * Keyed by column `key`, not by `CellKind`, because the exceptions above share a kind
 * with metrics that must NOT get their treatment — `cps`/`cpa` are `"money"` same as
 * Spend; `dda_orders`/`linear_orders` are `"count"` same as `triple_orders`.
 */
const DECIMAL_MONEY_KEYS = new Set(["cps", "cpa"]);
const DECIMAL_COUNT_KEYS = new Set(["dda_orders", "linear_orders"]);

export function formatCount(value: string | number | null | undefined, key?: string): string {
  if (key !== undefined && DECIMAL_COUNT_KEYS.has(key)) return formatFixed(value, 2);
  return formatWhole(value);
}

/** Money, in the store's own currency — see the note above on why no symbol appears. */
export function formatMoney(value: string | number | null | undefined, key?: string): string {
  if (key !== undefined && DECIMAL_MONEY_KEYS.has(key)) return formatFixed(value, 2);
  return formatWhole(value);
}

/** ROAS and its variants. Two decimals: the difference between 2.24 and 2.25 is a decision. */
export function formatRatio(value: string | number | null | undefined): string {
  return formatFixed(value, 2);
}

/**
 * Percentages. The API has ALREADY multiplied by 100 — `cvr_percentage` is `2.5` for
 * 2.5%, not `0.025` (see the `* 100.0` in packages/ch/src/reports). Multiplying again
 * here would inflate every conversion rate a hundredfold, so this only appends the sign.
 */
export function formatPercent(value: string | number | null | undefined): string {
  const text = formatFixed(value, 2);
  return text === BLANK ? BLANK : `${text}%`;
}

/**
 * Weeks are numbered from the START OF THE REQUESTED RANGE, not from a calendar Monday,
 * and `week_num` is 0-based. A merchant reading "Week 0" would reasonably think it meant
 * the week before the one they asked for.
 */
export function formatWeek(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return BLANK;
  const index = typeof value === "number" ? value : Number(value);
  return Number.isFinite(index) ? `Week ${index + 1}` : BLANK;
}

/** Plain text, with the empty string shown as absent rather than as a blank gap. */
export function formatText(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return BLANK;
  const text = String(value);
  return text.trim() === "" ? BLANK : text;
}

/**
 * Rounded, grouped, no decimals. `BigInt("12345").toLocaleString()` groups without ever
 * going through a float, so a count larger than `Number.MAX_SAFE_INTEGER` still renders
 * exactly — the same guarantee `formatCount` always made, now shared with `formatMoney`'s
 * non-decimal path too.
 */
function formatWhole(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return BLANK;
  if (typeof value === "number") return Number.isFinite(value) ? Math.round(value).toLocaleString(LOCALE) : BLANK;

  const trimmed = value.trim();
  if (trimmed === "") return BLANK;
  if (INTEGER.test(trimmed)) return BigInt(trimmed).toLocaleString(LOCALE);

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? Math.round(parsed).toLocaleString(LOCALE) : BLANK;
}

function formatFixed(value: string | number | null | undefined, digits: number): string {
  if (value === null || value === undefined || value === "") return BLANK;
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed)) return BLANK;
  return parsed.toLocaleString(LOCALE, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
